/**
 * User health profile — types + Zod validators (Step 17.1).
 *
 * Matches the `user_health_profile` Drizzle table in
 * `src/lib/db/schema.ts` (added in this same step). Categorical
 * enum values are validated here in the app layer rather than via
 * DB CHECK constraints so new values land without migrations.
 *
 * Two top-level Zod schemas:
 *
 *   healthProfileInputSchema     — every field optional. Use for
 *                                  the incremental-save API the
 *                                  Block 2 quiz UI will POST after
 *                                  each step. Enum fields are
 *                                  validated when present.
 *
 *   healthProfileCompleteSchema  — the "ready to flip
 *                                  completion_level='complete'"
 *                                  predicate. Requires at least one
 *                                  primary_concern + activity_level
 *                                  + diet_pattern + sleep_hours_avg
 *                                  + primary_goal.
 *
 * The HealthProfile + HealthProfileInput TS types below are derived
 * from the schemas via `z.infer<>` so the type signatures and
 * runtime validation stay in lockstep.
 */

import { z } from 'zod';

// ── Categorical enum schemas + types ──────────────────────────────────

export const PRIMARY_CONCERNS = [
  'energy',
  'sleep',
  'weight',
  'mood',
  'cognition',
  'digestion',
  'hormones',
  'heart',
  'immune',
  'skin',
  'inflammation',
  'longevity',
  'wellness_baseline',
] as const;
export const primaryConcernSchema = z.enum(PRIMARY_CONCERNS);
export type PrimaryConcern = z.infer<typeof primaryConcernSchema>;

export const ACTIVITY_LEVELS = ['sedentary', 'light', 'moderate', 'athletic'] as const;
export const activityLevelSchema = z.enum(ACTIVITY_LEVELS);
export type ActivityLevel = z.infer<typeof activityLevelSchema>;

export const DIET_PATTERNS = [
  'omnivore',
  'vegetarian',
  'vegan',
  'pescatarian',
  'keto',
  'mediterranean',
  'paleo',
  'other',
] as const;
export const dietPatternSchema = z.enum(DIET_PATTERNS);
export type DietPattern = z.infer<typeof dietPatternSchema>;

// Smoking + alcohol kept for backward-compat (existing rows + future
// clarity-AI capture surface). Track 5.Y-cat-quiz-amplification Phase 1
// removed these from the intro quiz; defer collection to a post-purchase
// clarity flow (Track 5.Y-cat-quiz-clarity-ai).
export const SMOKING_STATUSES = ['never', 'former', 'current'] as const;
export const smokingStatusSchema = z.enum(SMOKING_STATUSES);
export type SmokingStatus = z.infer<typeof smokingStatusSchema>;

export const ALCOHOL_FREQUENCIES = ['none', 'occasional', 'regular', 'daily'] as const;
export const alcoholFrequencySchema = z.enum(ALCOHOL_FREQUENCIES);
export type AlcoholFrequency = z.infer<typeof alcoholFrequencySchema>;

// Track 5.Y-cat-quiz-amplification Phase 1: 4 new quiz dimensions.
// Each enum value also serves as a funnel intent key — funnel_steps
// quiz_intent_filter values use these literals directly.
export const ENERGY_PATTERNS = [
  'steady',
  'morning_only',
  'persistent_drag',
  'afternoon_crash',
  'wired_tired',
] as const;
export const energyPatternSchema = z.enum(ENERGY_PATTERNS);
export type EnergyPattern = z.infer<typeof energyPatternSchema>;

// `cognitive_sharp` is source-namespaced (was `sharp`) to disambiguate
// from MEMORY_STATES.sharp at the deriveUserIntents flat-concat step —
// otherwise funnel_steps.quiz_intent_filter: ['sharp'] would match both
// axes. Renamed under Track 5.Y-cat-quiz-composer-tightening; zero
// persisted user_health_profile rows carried the old value at rename
// time (verified live on dev DB), and zero funnel_steps filtered on it.
export const COGNITIVE_STATES = [
  'cognitive_sharp',
  'solid_drift',
  'brain_fog',
  'severe_fog',
] as const;
export const cognitiveStateSchema = z.enum(COGNITIVE_STATES);
export type CognitiveState = z.infer<typeof cognitiveStateSchema>;

// `memory_sharp` is source-namespaced — see COGNITIVE_STATES note above.
export const MEMORY_STATES = [
  'memory_sharp',
  'occasional_misplace',
  'forget_names',
  'worried_decline',
] as const;
export const memoryStateSchema = z.enum(MEMORY_STATES);
export type MemoryState = z.infer<typeof memoryStateSchema>;

export const SLEEP_HOURS_BANDS = [
  'under_5',
  '5_to_6',
  '6_to_7',
  '7_to_8',
  '8_to_9',
  'over_9',
] as const;
export const sleepHoursBandSchema = z.enum(SLEEP_HOURS_BANDS);
export type SleepHoursBand = z.infer<typeof sleepHoursBandSchema>;

export const BIOMETRICS_UNIT_PREFS = ['imperial', 'metric'] as const;
export const biometricsUnitPrefSchema = z.enum(BIOMETRICS_UNIT_PREFS);
export type BiometricsUnitPref = z.infer<typeof biometricsUnitPrefSchema>;

export const MENSTRUAL_STATUSES = [
  'regular',
  'irregular',
  'pregnant',
  'breastfeeding',
  'postmenopausal',
  'perimenopausal',
  'not_applicable',
] as const;
export const menstrualStatusSchema = z.enum(MENSTRUAL_STATUSES);
export type MenstrualStatus = z.infer<typeof menstrualStatusSchema>;

export const CYCLE_PHASES = [
  'follicular',
  'luteal',
  'ovulatory',
  'menstrual',
  'not_tracking',
] as const;
export const cyclePhaseSchema = z.enum(CYCLE_PHASES);
export type CyclePhase = z.infer<typeof cyclePhaseSchema>;

export const BIRTH_CONTROL_TYPES = [
  'none',
  'oral',
  'iud_hormonal',
  'iud_copper',
  'implant',
  'shot',
  'patch',
  'ring',
  'other',
] as const;
export const birthControlTypeSchema = z.enum(BIRTH_CONTROL_TYPES);
export type BirthControlType = z.infer<typeof birthControlTypeSchema>;

export const DIAGNOSED_CONDITIONS = [
  'diabetes_t1',
  'diabetes_t2',
  'prediabetes',
  'hypothyroidism',
  'hyperthyroidism',
  'pcos',
  'endometriosis',
  'celiac',
  'ibs',
  'ibd',
  'hypertension',
  'high_cholesterol',
  'autoimmune_other',
  'cancer_history',
  'kidney_disease',
  'liver_disease',
  'heart_disease',
  'anemia',
  'none',
] as const;
export const diagnosedConditionSchema = z.enum(DIAGNOSED_CONDITIONS);
export type DiagnosedCondition = z.infer<typeof diagnosedConditionSchema>;

export const FAMILY_HISTORY_CATEGORIES = [
  'diabetes',
  'heart_disease',
  'cancer',
  'thyroid',
  'autoimmune',
  'mental_health',
  'none',
  'unknown',
] as const;
export const familyHistoryCategorySchema = z.enum(FAMILY_HISTORY_CATEGORIES);
export type FamilyHistoryCategory = z.infer<typeof familyHistoryCategorySchema>;

export const TESTING_RECENCIES = [
  'within_3_months',
  'within_12_months',
  'over_1_year',
  'never',
] as const;
export const testingRecencySchema = z.enum(TESTING_RECENCIES);
export type TestingRecency = z.infer<typeof testingRecencySchema>;

export const PRIMARY_GOALS = [
  'baseline_health',
  'optimize_performance',
  'investigate_symptom',
  'track_over_time',
  'preventive_screening',
  'longevity',
  'other',
] as const;
export const primaryGoalSchema = z.enum(PRIMARY_GOALS);
export type PrimaryGoal = z.infer<typeof primaryGoalSchema>;

export const COMPLETION_LEVELS = ['empty', 'partial', 'complete'] as const;
export const completionLevelSchema = z.enum(COMPLETION_LEVELS);
export type CompletionLevel = z.infer<typeof completionLevelSchema>;

// ── Numeric/text bounds (sanity, not clinical limits) ─────────────────

const sleepHoursSchema = z.number().min(0).max(24);
const heightCmSchema = z.number().min(50).max(260);   // 1'8" to 8'6"
const weightKgSchema = z.number().min(20).max(400);   // 44lb to 880lb
const lmpSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const notesSchema = z.string().max(2000);

// ── Incremental-save schema (every field optional) ────────────────────

/**
 * Use this from the /api/health-profile/save route called after each
 * quiz step. Empty arrays are valid. Enum fields are validated only
 * when present.
 */
export const healthProfileInputSchema = z.object({
  primaryConcerns: z.array(primaryConcernSchema).max(13).optional(),

  activityLevel: activityLevelSchema.optional(),
  dietPattern: dietPatternSchema.optional(),
  sleepHoursAvg: sleepHoursSchema.optional(),
  smokingStatus: smokingStatusSchema.optional(),
  alcoholFrequency: alcoholFrequencySchema.optional(),

  // Phase 1 dimensions
  energyPattern: energyPatternSchema.optional(),
  cognitiveState: cognitiveStateSchema.optional(),
  memoryState: memoryStateSchema.optional(),
  sleepHoursBand: sleepHoursBandSchema.optional(),

  heightCm: heightCmSchema.optional(),
  weightKg: weightKgSchema.optional(),
  biometricsUnitPref: biometricsUnitPrefSchema.optional(),

  menstrualStatus: menstrualStatusSchema.optional(),
  cyclePhase: cyclePhaseSchema.optional(),
  lastMenstrualPeriod: lmpSchema.optional(),
  birthControlType: birthControlTypeSchema.optional(),

  diagnosedConditions: z.array(diagnosedConditionSchema).max(DIAGNOSED_CONDITIONS.length).optional(),
  medicationsNotes: notesSchema.optional(),
  familyHistory: z.array(familyHistoryCategorySchema).max(FAMILY_HISTORY_CATEGORIES.length).optional(),

  testedRecently: z.boolean().optional(),
  testingRecency: testingRecencySchema.optional(),

  // Step 33B: multi-select (was primaryGoal singular pre-migration 0011).
  primaryGoals: z.array(primaryGoalSchema).max(PRIMARY_GOALS.length).optional(),
  goalsNotes: notesSchema.optional(),
});
export type HealthProfileInput = z.infer<typeof healthProfileInputSchema>;

// ── Completion predicate ──────────────────────────────────────────────

/**
 * Predicate the API checks before flipping completion_level to
 * 'complete'. Minimal viable answer set:
 *   - at least one primary concern picked
 *   - activity_level + diet_pattern + sleep_hours
 *   - primary_goal
 *
 * Everything else is genuinely optional and won't block the flip.
 */
// Phase 1: completion now requires the 4 new dimensions in place of the
// numeric sleep slider. Smoking + alcohol drop from the predicate (no
// longer collected at quiz time). sleepHoursAvg drops too; replaced by
// sleepHoursBand. activity_level + diet_pattern + goals retained.
export const healthProfileCompleteSchema = z.object({
  primaryConcerns: z.array(primaryConcernSchema).min(1, 'Pick at least one concern'),
  activityLevel: activityLevelSchema,
  dietPattern: dietPatternSchema,
  energyPattern: energyPatternSchema,
  cognitiveState: cognitiveStateSchema,
  memoryState: memoryStateSchema,
  sleepHoursBand: sleepHoursBandSchema,
  primaryGoals: z.array(primaryGoalSchema).min(1, 'Pick at least one goal'),
});
export type HealthProfileComplete = z.infer<typeof healthProfileCompleteSchema>;

// ── Full row shape (mirrors the Drizzle row, used by reads) ───────────

export interface HealthProfile {
  id: string;
  userId: string;

  primaryConcerns: PrimaryConcern[];

  activityLevel: ActivityLevel | null;
  dietPattern: DietPattern | null;
  /** Drizzle returns numeric() as string — caller parses if needed. */
  sleepHoursAvg: string | null;
  smokingStatus: SmokingStatus | null;
  alcoholFrequency: AlcoholFrequency | null;

  energyPattern: EnergyPattern | null;
  cognitiveState: CognitiveState | null;
  memoryState: MemoryState | null;
  sleepHoursBand: SleepHoursBand | null;

  heightCm: string | null;
  weightKg: string | null;
  biometricsUnitPref: BiometricsUnitPref | null;

  menstrualStatus: MenstrualStatus | null;
  cyclePhase: CyclePhase | null;
  /** ISO date string YYYY-MM-DD. */
  lastMenstrualPeriod: string | null;
  birthControlType: BirthControlType | null;

  diagnosedConditions: DiagnosedCondition[];
  medicationsNotes: string | null;
  familyHistory: FamilyHistoryCategory[];

  testedRecently: boolean | null;
  testingRecency: TestingRecency | null;

  primaryGoals: PrimaryGoal[];
  goalsNotes: string | null;

  completionLevel: CompletionLevel;
  completedAt: Date | null;
  lastUpdatedAt: Date;
  createdAt: Date;
}
